import { useId, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * El panel donde se ve la API key en claro. **La única vez que se la puede ver.**
 *
 * # POR QUÉ ES UNA SOLA VEZ
 *
 * En la base va **solo el hash SHA-256** de la key más un prefijo de 12
 * caracteres para poder identificarla en un log. La devuelven el `201` del alta
 * y el `200` de la rotación, y **ningún otro endpoint la devuelve jamás**: el
 * `Sistema` del contrato no tiene un campo donde ponerla. Eso es estructural, no
 * un olvido — no hay un `GET /sistemas/{id}/key` que alguien se haya olvidado de
 * escribir, y no puede haberlo.
 *
 * # QUÉ EXIGE ESO DE ESTA PANTALLA
 *
 * 1. **Obligar a copiarla antes de seguir.** El panel tapa la pantalla y no se
 *    cierra hasta que la persona confirme que la guardó. Es fricción a
 *    propósito: un panel que se pueda cerrar de un clic distraído hace perder
 *    una credencial recién creada.
 * 2. **Decir con todas las letras que no se recupera.** Sin eso, la reacción
 *    natural es "después la busco en la pantalla del sistema".
 * 3. **Decir que perderla NO es un desastre.** Se rota y listo. Si no se dice,
 *    quien la pierde asume que perdió el sistema entero y lo crea de nuevo —
 *    dejando el anterior activo, con una key viva que nadie sabe dónde está.
 *
 * # DÓNDE NO VA LA KEY
 *
 * No entra a `localStorage` ni a `sessionStorage` (nada del front escribe ahí:
 * ni el JWT lo hace), no va a la URL, no se cachea en TanStack Query —viene de
 * una **mutación**, no de una query— y no se escribe en ningún log ni servicio
 * de errores. Vive en el estado de esa mutación mientras el panel está abierto y
 * se descarta con `reset()` al cerrarlo: por eso este componente recibe la key
 * por prop y **no la copia a un estado propio**. Hay tests que lo fijan.
 *
 * # EL PORTAPAPELES PUEDE NO ESTAR
 *
 * `navigator.clipboard` no existe fuera de contextos seguros y puede fallar por
 * permisos. Por eso el botón de copiar es una comodidad y **no la condición para
 * cerrar**: la condición es que la persona confirme, y siempre se puede
 * seleccionar el texto a mano. Atar el cierre a que el copiado funcione dejaría
 * el panel trabado justo cuando el navegador no colabora, con la key en
 * pantalla y sin salida.
 *
 * # VA POR UN PORTAL
 *
 * Lo montan una fila de la tabla y el formulario de alta, o sea que sin portal
 * quedaría un `<div>` dentro de un `<tbody>` y otro dentro de un `<form>`: HTML
 * inválido, y con el submit del formulario a un `Enter` de distancia mientras se
 * mira un secreto. Al `document.body`, que es donde va un diálogo que tapa la
 * página.
 */
export function KeyEnClaro({
  apiKey,
  nombre,
  motivo,
  alCerrar,
}: {
  /** La key en claro. **No se guarda en ningún lado**: se muestra y se olvida. */
  apiKey: string;
  /** Nombre del sistema, para que se sepa de cuál es la key. */
  nombre: string;
  /** De dónde salió: cambia lo que hay que hacer con ella. */
  motivo: 'alta' | 'rotacion';
  /** Cierra el panel. Quien lo pase tiene que **descartar la key** ahí. */
  alCerrar: () => void;
}) {
  const idTitulo = useId();
  const idKey = useId();
  const idAviso = useId();
  const [guardada, setGuardada] = useState(false);
  const [copiada, setCopiada] = useState<'no' | 'si' | 'falló'>('no');

  async function copiar(): Promise<void> {
    try {
      await navigator.clipboard.writeText(apiKey);
      setCopiada('si');
      // Copiar cuenta como haberla guardado: ya está fuera de la página.
      setGuardada(true);
    } catch {
      // Sin portapapeles se copia a mano. El cierre nunca dependió de esto.
      setCopiada('falló');
    }
  }

  return createPortal(
    <div className="fondo-modal">
      <div
        className="modal"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={idTitulo}
        aria-describedby={idAviso}
      >
        <h2 id={idTitulo}>
          {motivo === 'alta' ? 'Guardá la API key de ' : 'Guardá la API key nueva de '}
          {nombre}
        </h2>

        <p id={idAviso} className="error">
          Esta es la única vez que se puede ver. No se guarda en ningún lado: el servicio conserva
          solo un hash, así que no hay forma de volver a mostrarla.
        </p>

        <p>
          <label htmlFor={idKey}>API key</label>
          <br />
          <input
            id={idKey}
            className="key"
            readOnly
            value={apiKey}
            // Un secreto no se autocompleta ni se corrige.
            autoComplete="off"
            spellCheck={false}
            onFocus={(e) => e.currentTarget.select()}
          />
        </p>

        <p>
          <button type="button" onClick={() => void copiar()}>
            Copiar la key
          </button>{' '}
          {copiada === 'si' ? <span role="status">Copiada al portapapeles.</span> : null}
          {copiada === 'falló' ? (
            <span role="status" className="nota">
              El navegador no dejó copiar. Seleccioná el texto y copialo a mano.
            </span>
          ) : null}
        </p>

        {motivo === 'rotacion' ? (
          <p className="nota">
            La key anterior ya no sirve. La aplicación que la esté usando va a recibir un error de
            autenticación hasta que se le configure esta.
          </p>
        ) : (
          <p className="nota">
            Configurala en la aplicación que va a emitir. Va en el header{' '}
            <code>Authorization: Bearer</code> de cada llamada.
          </p>
        )}

        <p className="nota">
          Si la perdés no perdiste el sistema: se le rota la key desde esta misma pantalla y se
          configura la nueva. Lo único que cuesta es que la aplicación queda sin poder emitir hasta
          que la actualicen.
        </p>

        <p>
          <label>
            <input
              type="checkbox"
              checked={guardada}
              onChange={(e) => setGuardada(e.target.checked)}
            />{' '}
            Ya la guardé en un lugar seguro
          </label>
        </p>

        <button
          type="button"
          // No se cierra hasta confirmar. La fricción es el punto: cerrar sin
          // haberla guardado obliga a rotar, y rotar deja la app afuera.
          disabled={!guardada}
          onClick={alCerrar}
        >
          Listo
        </button>
      </div>
    </div>,
    document.body,
  );
}
